export const urlToFile = async (url: string, filename: string): Promise<File> => {
  const blob = await (await fetch(url)).blob();
  return new File([blob], filename, {type: blob.type});
};

export const dataUrlToBlob = (dataUrl: string): Blob => {
  const [header, base64Data] = dataUrl.split(',');
  const mimeType = /data:(.*?);/.exec(header)?.[1] ?? 'application/octet-stream';
  const binary = atob(base64Data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], {type: mimeType});
};

/**
 * Beside `dataUrlToBlob`, its inverse. Lives in this module because it has no imports of its own, so a bundle can read a blob without pulling in the imaging stack
 * `@Utils/image.util` reaches for. Takes a `Blob` so a picked file and a fetched response body travel the same path — a `File` is already one.
 */
export const blobToDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('The blob could not be read as a data URL'));
    };
    reader.onerror = (): void => {
      reject(new Error('The blob could not be read'));
    };
    reader.readAsDataURL(blob);
  });
};

export const downloadBlobAsFile = (blob: Blob, filename: string): void => {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

export const doesPublicFileExistAsync = (url: string, timeout = 0): Promise<boolean> => {
  return new Promise((resolve) => {
    try {
      const xmlHttp = new XMLHttpRequest();

      xmlHttp.ontimeout = (_) => {
        return resolve(false);
      };
      xmlHttp.onerror = (_) => {
        return resolve(false);
      };

      xmlHttp.onreadystatechange = function () {
        if (xmlHttp.readyState === 4) {
          resolve(xmlHttp.status === 200);
        }
      };

      xmlHttp.open('GET', url, true);
      xmlHttp.timeout = timeout;
      xmlHttp.send(null);
    } catch (err) {
      resolve(false);
    }
  });
};

export const isSvgFile = (file: File): boolean => {
  return file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
};

export const isHeicImageFile = (file: File): boolean => {
  return file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic');
};

/**
 * Mirrors PHP make_filename_safe: remove leading hyphens and any disallowed characters
 * Allowed: alphanumeric, underscore, space, parentheses, hyphen. When hyphenate=true, replace removed chars with '-'.
 */
export function makeFilenameSafe(name: string, defaultValue: string, hyphenate = false): string {
  const replacement = hyphenate ? '-' : '';
  const safe = name.replace(/^-+|[^\w_ ()-]+/g, replacement).trim();
  return safe !== '' ? safe : defaultValue;
}
